import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';

import styles from '../../../styles/DidYouKnow.module.css';

/**
 * "Did You Know?" Facts Component
 * Shows one interesting fact per race to encourage voter education
 */
function DidYouKnow({ office, _district, lastVotes = null }) {
  const getFact = useCallback((off, votes) => {
    const getFactDatabase = () => ({
      'U.S. Representative': [
        'Representatives serve 2-year terms and elections happen every even-numbered year.',
        'Your U.S. Representative must live in the state they represent.',
        'House members represent approximately 760,000 people on average.',
        'The House has 435 voting members.',
        'Representatives can be as young as 25 years old.',
      ],
      'U.S. Senate': [
        'Senators serve 6-year terms, with elections every 2 years for one-third of seats.',
        'Both senators represent the entire state, not a district.',
        'Senators must be at least 30 years old.',
        'Each state has exactly 2 senators, regardless of population.',
        'The Senate has 100 members.',
      ],
      Governor: [
        'Most governors serve 4-year terms.',
        'Governors command the state National Guard.',
        'Governors can veto state legislation.',
        'The governor is the chief executive of the state.',
        'Governors typically serve no more than 2 terms.',
      ],
      'State Senate': [
        'State senators represent their legislative district.',
        'State Senate districts are much smaller than federal districts.',
        'State senators typically serve 2 or 4-year terms depending on the state.',
        'State senators create and pass state laws.',
        'Most states have between 30-50 state senators.',
      ],
      'State House': [
        'State House members represent smaller districts than state senators.',
        'State House seats often turn over more frequently than higher offices.',
        'This is often a stepping stone to higher office.',
        'State House members create much of the local legislation affecting daily life.',
        'There are usually 4-5 times more House members than Senate members.',
      ],
      'School Board': [
        'School Board members are often elected at-large or by district.',
        'The School Board hires the Superintendent and sets education policy.',
        'School Board meetings are public and open to attendance.',
        'School Board elections often have the lowest voter turnout.',
        'School Board decisions affect property taxes and school funding.',
      ],
      'City Council': [
        'City Council members typically represent districts or serve at-large.',
        'City Council votes on local ordinances and budgets.',
        'City Council members often serve 2 or 4-year terms.',
        'The City Council works with the Mayor on local issues.',
        'Local elections have some of the lowest turnout rates.',
      ],
      'County Commissioner': [
        'County Commissioners oversee county operations and budgets.',
        'Counties typically have 3-5 commissioners depending on size.',
        'County government handles services like roads, courts, and welfare.',
        'County-level elections are less publicized than federal elections.',
        'County decisions directly impact local services and taxes.',
      ],
    });

    const getHistoryFacts = () => [
      'Did you know? Voting is one of the most powerful rights in a democracy.',
      'Lower-level elections often have the biggest impact on your daily life.',
      'Local elections frequently decide issues that directly affect your community.',
      'Voting in off-year elections helps ensure your voice is heard year-round.',
      'Small local elections can be decided by just a few votes.',
    ];

    const allFacts = getFactDatabase();
    const allHistory = getHistoryFacts();
    const officeKey = Object.keys(allFacts).find(
      (key) => off?.includes(key) || off?.toLowerCase().includes(key.toLowerCase())
    );

    if (officeKey) {
      const facts = allFacts[officeKey];
      return facts[Math.floor(Math.random() * facts.length)];
    }
    if (votes !== null) {
      return `In the last election, this race had ${votes.toLocaleString()} votes cast.`;
    }
    return allHistory[Math.floor(Math.random() * allHistory.length)];
  }, []);

  const [fact, setFact] = useState(() => getFact(office, lastVotes));

  useEffect(() => {
    setFact(getFact(office, lastVotes));
  }, [office, lastVotes, getFact]);

  if (!fact) return null;

  return (
    <div className={styles.container}>
      <div className={styles.icon}>💡</div>
      <div className={styles.content}>
        <p className={styles.label}>Did you know?</p>
        <p className={styles.fact}>{fact}</p>
      </div>
    </div>
  );
}

DidYouKnow.propTypes = {
  office: PropTypes.string,
  _district: PropTypes.string,
  lastVotes: PropTypes.number,
};

DidYouKnow.defaultProps = {
  office: null,
  _district: null,
  lastVotes: null,
};

export default DidYouKnow;
